import { format } from "date-fns";
import { ja } from "date-fns/locale";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import JSZip from "jszip";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "urql";
import { ChevronLeftIcon } from "../../components/Icons";
import { TermReportQuery } from "../../graphql/report.graphql";
import { logger } from "../../lib/logger";

export function TermReport() {
  const navigate = useNavigate();
  const params = useParams();
  const termId = params.termId;
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);

  const [result] = useQuery({
    query: TermReportQuery,
    variables: { termId: termId ?? "" },
    pause: !termId,
  });

  const report = result.data?.termReport;
  const interviews = report?.interviews ?? [];
  const loading = result.fetching;

  useEffect(() => {
    if (interviews.length === 0) return;

    const observerOptions = {
      root: null,
      rootMargin: "-100px 0px -50% 0px",
      threshold: 0.3,
    };

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      }
    }, observerOptions);

    for (const interview of interviews) {
      if (!interview.candidate) continue;
      const element = document.getElementById(
        `candidate-${interview.candidate.candidateId}`,
      );
      if (element) {
        observer.observe(element);
      }
    }

    return () => {
      for (const interview of interviews) {
        if (!interview.candidate) continue;
        const element = document.getElementById(
          `candidate-${interview.candidate.candidateId}`,
        );
        if (element) {
          observer.unobserve(element);
        }
      }
    };
  }, [interviews]);

  const scrollToSection = (candidateId: string) => {
    const element = document.getElementById(`candidate-${candidateId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveSection(`candidate-${candidateId}`);
    }
  };

  const elementToCanvas = async (
    element: HTMLElement,
  ): Promise<HTMLCanvasElement> => {
    element.classList.add("pdf-export");

    const allElements = element.querySelectorAll("*");
    const styleBackups: Map<HTMLElement, string> = new Map();

    for (const el of allElements) {
      const htmlEl = el as HTMLElement;
      const originalStyle = htmlEl.getAttribute("style") || "";
      styleBackups.set(htmlEl, originalStyle);

      try {
        const computedStyle = window.getComputedStyle(htmlEl);

        const bgColor = computedStyle.backgroundColor;
        if (
          bgColor &&
          bgColor !== "rgba(0, 0, 0, 0)" &&
          bgColor !== "transparent"
        ) {
          htmlEl.style.setProperty("background-color", bgColor, "important");
        }

        const textColor = computedStyle.color;
        if (textColor) {
          htmlEl.style.setProperty("color", textColor, "important");
        }

        const borderColor = computedStyle.borderColor;
        if (borderColor && borderColor !== "rgba(0, 0, 0, 0)") {
          htmlEl.style.setProperty("border-color", borderColor, "important");
        }

        const lineHeight = computedStyle.lineHeight;
        if (lineHeight && lineHeight !== "normal") {
          htmlEl.style.setProperty("line-height", lineHeight, "important");
        }

        const verticalAlign = computedStyle.verticalAlign;
        if (verticalAlign) {
          htmlEl.style.setProperty(
            "vertical-align",
            verticalAlign,
            "important",
          );
        }
      } catch {
        // Skip on error
      }
    }

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
    });

    element.classList.remove("pdf-export");
    for (const [el, originalStyle] of styleBackups) {
      if (originalStyle) {
        el.setAttribute("style", originalStyle);
      } else {
        el.removeAttribute("style");
      }
    }

    return canvas;
  };

  const createPDFForCandidate = async (
    interview: (typeof interviews)[number],
  ): Promise<jsPDF> => {
    if (!interview.candidate) {
      throw new Error("候補者情報がありません");
    }
    const element = document.getElementById(
      `candidate-${interview.candidate.candidateId}`,
    );
    if (!element) {
      throw new Error(
        `候補者 ${interview.candidate.name} の要素が見つかりません`,
      );
    }

    const canvas = await elementToCanvas(element as HTMLElement);
    const imgData = canvas.toDataURL("image/png");

    const imgWidth = 210;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const pdf = new jsPDF("p", "mm", [imgWidth, imgHeight]);
    pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);

    return pdf;
  };

  const exportToZip = async () => {
    if (!report || interviews.length === 0) return;

    setExporting(true);
    try {
      const zip = new JSZip();
      const folderName = report.title;
      const folder = zip.folder(folderName);

      if (!folder) {
        throw new Error("ZIPフォルダの作成に失敗しました");
      }

      for (const interview of interviews) {
        if (!interview.candidate) continue;
        try {
          const pdf = await createPDFForCandidate(interview);
          const pdfBlob = pdf.output("blob");
          const fileName = `${interview.candidate.name}.pdf`;
          folder.file(fileName, pdfBlob);
        } catch (err) {
          logger.error("候補者のPDF生成に失敗", {
            candidateName: interview.candidate.name,
            err,
          });
        }
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(zipBlob);
      link.download = `${folderName}_レポート_${format(new Date(), "yyyyMMdd", { locale: ja })}.zip`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (error) {
      logger.error("ZIPエクスポートエラー", { err: error });
      alert("ZIPファイルの生成に失敗しました");
    } finally {
      setExporting(false);
    }
  };

  const renderScoreBoxes = (score: number) => {
    // 要約チェック画面のスコアボタンは 1〜3 の整数値。
    // 旧実装 (`> 3 → 3 / > 1 → 2 / else → 1`) は score=3 が >1 分岐に落ちて 2 に
    // 丸められ ◎(s=3) が点灯しなかったため、3 段階のバケットを明示する。
    let displayScore: 1 | 2 | 3;
    if (score >= 3) {
      displayScore = 3;
    } else if (score >= 2) {
      displayScore = 2;
    } else {
      displayScore = 1;
    }

    const symbols = ["◎", "○", "△"];
    const scores = [3, 2, 1];

    return (
      <div className="inline-block">
        {scores.map((s, idx) => (
          <div
            key={s}
            className={`inline-block h-12 w-12 rounded-lg text-center leading-[44px] ${
              displayScore === s
                ? "border-2 border-white bg-[var(--color-orange-500)]"
                : "border-2 border-[#ddd] bg-white"
            } ${idx < 2 ? "mr-2" : ""}`}
          >
            <span
              className={`align-middle font-normal text-2xl ${
                displayScore === s
                  ? "text-white"
                  : "text-[var(--color-text-secondary)]"
              }`}
            >
              {symbols[idx]}
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-[var(--color-bg-subtle-gray)] p-6">
        <div className="flex items-center justify-center p-12">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-[var(--color-primary-500)] border-t-transparent" />
            <p className="font-medium text-[var(--color-text-secondary)]">
              読み込み中...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (result.error) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-[var(--color-bg-subtle-gray)] p-6">
        <div className="flex items-center justify-center p-12">
          <div className="flex flex-col items-center gap-4">
            <p className="font-medium text-red-600">
              レポートの読み込みに失敗しました
            </p>
            <button
              type="button"
              onClick={() => navigate(`/employee_interview/terms/${termId}`)}
              className="rounded-lg border border-[var(--color-primary-500)] px-4 py-2 font-medium text-[var(--color-primary-500)] text-sm"
            >
              戻る
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-[var(--color-bg-subtle-gray)] p-6">
        <div className="flex items-center justify-center p-12">
          <div className="flex flex-col items-center gap-4">
            <p className="font-medium text-[var(--color-text-secondary)]">
              レポートが見つかりませんでした
            </p>
            <button
              type="button"
              onClick={() => navigate(`/employee_interview/terms/${termId}`)}
              className="rounded-lg border border-[var(--color-primary-500)] px-4 py-2 font-medium text-[var(--color-primary-500)] text-sm"
            >
              戻る
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[var(--color-bg-subtle-gray)]">
      <div className="mx-auto flex min-h-[calc(100vh-200px)] max-w-[1240px]">
        <div className="sticky top-[100px] mr-6 h-fit w-[200px] border-[var(--color-border-default)] border-r bg-[var(--color-bg-subtle-gray)] p-4">
          <h3 className="mb-4 font-bold text-[var(--color-primary-500)] text-sm">
            目次
          </h3>
          <div className="space-y-2">
            {interviews
              .filter((interview) => interview.candidate)
              .map((interview) => (
                <button
                  type="button"
                  key={interview.interviewId}
                  onClick={() => {
                    if (interview.candidate) {
                      scrollToSection(interview.candidate.candidateId);
                    }
                  }}
                  className={`w-full cursor-pointer rounded-md border-none px-3 py-2.5 text-left text-[13px] transition-all duration-200 ${
                    activeSection ===
                    `candidate-${interview.candidate?.candidateId}`
                      ? "bg-[var(--color-primary-50)] font-bold text-[var(--color-primary-500)]"
                      : "bg-transparent text-[#222] hover:bg-[#f0f0f0]"
                  }`}
                >
                  {interview.candidate?.name}
                </button>
              ))}
          </div>
        </div>

        <div className="flex-1 p-6">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <button
                type="button"
                onClick={() => navigate(`/employee_interview/terms/${termId}`)}
                className="flex items-center gap-1 rounded-lg border border-[var(--color-primary-500)] px-4 py-2 font-medium text-[var(--color-primary-500)] text-sm transition-colors hover:bg-[var(--color-bg-subtle-gray)]"
              >
                <ChevronLeftIcon className="h-4 w-4" />
                戻る
              </button>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={exportToZip}
                disabled={interviews.length === 0 || exporting}
                className={`rounded-lg border-none px-4 py-2 font-medium text-sm text-white transition-colors ${
                  interviews.length === 0 || exporting
                    ? "cursor-not-allowed bg-[#ccc]"
                    : "cursor-pointer bg-[var(--color-primary-500)]"
                }`}
              >
                {exporting ? "エクスポート中..." : "ZIPエクスポート"}
              </button>
            </div>
          </div>

          <div className="mb-8 border-[var(--color-primary-500)] border-l-4 pl-5">
            <h1 className="mb-2 font-bold text-[32px] text-[var(--color-primary-500)]">
              {report.title}
            </h1>
            <p className="font-medium text-[var(--color-text-secondary)] text-sm">
              インタビューレポート
            </p>
          </div>

          <div ref={contentRef} data-pdf-content className="report-content">
            {interviews.length === 0 ? (
              <div className="rounded-xl border border-[var(--color-border-default)] bg-white px-6 py-12 text-center">
                <p className="text-[var(--color-text-secondary)]">
                  要約チェック完了済みのインタビューがありません。
                </p>
              </div>
            ) : (
              <div className="space-y-8">
                {interviews
                  .filter((interview) => interview.candidate)
                  .map((interview) => (
                    <div
                      key={interview.interviewId}
                      id={`candidate-${interview.candidate?.candidateId}`}
                      className="scroll-mt-[100px] overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]"
                    >
                      <div className="border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
                        <div className="flex items-start justify-between">
                          <div>
                            <h2 className="mb-1.5 font-bold text-2xl text-[var(--color-primary-500)]">
                              {interview.candidate?.name}
                            </h2>
                            <p className="font-medium text-[13px] text-[var(--color-text-secondary)]">
                              {interview.candidate?.email}
                            </p>
                          </div>
                          {interview.completedAt && (
                            <p className="font-medium text-[var(--color-text-secondary)] text-xs">
                              {format(
                                new Date(interview.completedAt),
                                "yyyy年MM月dd日",
                                { locale: ja },
                              )}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="p-6">
                        {(interview.satisfactionScore ||
                          interview.behaviorChangeScore) && (
                          <div className="mb-8 rounded-[10px] border border-[#b3d9f0] bg-[var(--color-primary-50)] p-5">
                            <h3 className="mb-4 font-bold text-[var(--color-primary-500)] text-base">
                              評価スコア
                            </h3>
                            <div className="block">
                              {interview.satisfactionScore && (
                                <div className="mr-8 inline-block text-center">
                                  {renderScoreBoxes(
                                    interview.satisfactionScore,
                                  )}
                                  <div className="mt-2 text-center font-medium text-[13px] text-[var(--color-text-secondary)]">
                                    満足度
                                  </div>
                                </div>
                              )}
                              {interview.behaviorChangeScore && (
                                <div className="inline-block text-center">
                                  {renderScoreBoxes(
                                    interview.behaviorChangeScore,
                                  )}
                                  <div className="mt-2 text-center font-medium text-[13px] text-[var(--color-text-secondary)]">
                                    行動変容
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="space-y-6">
                          {interview.summaryItems.filter(
                            (item) => item.category === "CHALLENGE",
                          ).length > 0 && (
                            <div className="mb-7">
                              <h3 className="mb-4 border-[var(--color-orange-500)] border-l-4 pl-4 font-bold text-[var(--color-orange-500)] text-lg">
                                課題
                              </h3>
                              {interview.summaryItems
                                .filter((item) => item.category === "CHALLENGE")
                                .map((item) => (
                                  <div
                                    key={item.summaryItemId}
                                    className="mb-4 rounded-lg border border-[#f0f0f0] bg-white p-4"
                                  >
                                    <p className="whitespace-pre-wrap text-[#222222] text-[15px] leading-[1.9]">
                                      {item.content}
                                    </p>
                                  </div>
                                ))}
                            </div>
                          )}

                          {interview.summaryItems.filter(
                            (item) => item.category === "CHANGE",
                          ).length > 0 && (
                            <div className="mb-7">
                              <h3 className="mb-4 border-[var(--color-orange-500)] border-l-4 pl-4 font-bold text-[var(--color-orange-500)] text-lg">
                                変化
                              </h3>
                              {interview.summaryItems
                                .filter((item) => item.category === "CHANGE")
                                .map((item) => (
                                  <div
                                    key={item.summaryItemId}
                                    className="mb-4 rounded-lg border border-[#f0f0f0] bg-white p-4"
                                  >
                                    <p className="whitespace-pre-wrap text-[#222222] text-[15px] leading-[1.9]">
                                      {item.content}
                                    </p>
                                  </div>
                                ))}
                            </div>
                          )}

                          {interview.summaryItems.filter(
                            (item) => item.category === "ACHIEVEMENT",
                          ).length > 0 && (
                            <div className="mb-7">
                              <h3 className="mb-4 border-[var(--color-orange-500)] border-l-4 pl-4 font-bold text-[var(--color-orange-500)] text-lg">
                                成果
                              </h3>
                              {interview.summaryItems
                                .filter(
                                  (item) => item.category === "ACHIEVEMENT",
                                )
                                .map((item) => (
                                  <div
                                    key={item.summaryItemId}
                                    className="mb-4 rounded-lg border border-[#f0f0f0] bg-white p-4"
                                  >
                                    <p className="whitespace-pre-wrap text-[#222222] text-[15px] leading-[1.9]">
                                      {item.content}
                                    </p>
                                  </div>
                                ))}
                            </div>
                          )}

                          {interview.summaryItems.filter(
                            (item) =>
                              item.category === "SATISFACTION" ||
                              item.category === "REASON",
                          ).length > 0 && (
                            <div className="mb-7">
                              <h3 className="mb-4 border-[var(--color-orange-500)] border-l-4 pl-4 font-bold text-[var(--color-orange-500)] text-lg">
                                満足度の理由
                              </h3>
                              {interview.summaryItems
                                .filter(
                                  (item) =>
                                    item.category === "SATISFACTION" ||
                                    item.category === "REASON",
                                )
                                .map((item) => (
                                  <div
                                    key={item.summaryItemId}
                                    className="mb-4 rounded-lg border border-[#f0f0f0] bg-white p-4"
                                  >
                                    <p className="whitespace-pre-wrap text-[#222222] text-[15px] leading-[1.9]">
                                      {item.content}
                                    </p>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
